// Recommendation fetching is independent of variant preloading and purchase state.
(() => {
  class RecommendedProducts extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const complete = this.hasAttribute('data-recommendation-shell') && this.hasAttribute('data-loaded');
      const url = complete || this.hasAttribute('data-manual-products') ? null : this.requestUrl();
      if (!url && !this.querySelector('[data-card-src]')) return;
      let started = false;
      const load = () => {
        if (started || signal.aborted || !this.isConnected) return;
        started = true;
        this.observer?.disconnect();
        this.querySelectorAll('[data-card-src]').forEach(image => {
          if (image.dataset.cardSrcset) image.srcset = image.dataset.cardSrcset;
          image.src = image.dataset.cardSrc;
          image.removeAttribute('data-card-srcset');
          image.removeAttribute('data-card-src');
          image.removeAttribute('aria-hidden');
        });
        if (url) this.load(url, signal);
      };
      this.addEventListener('focusin', load, { once: true, signal });
      if (!('IntersectionObserver' in window) || window.Shopify?.designMode) load();
      else {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) load();
        }, { rootMargin: '600px' });
        this.observer.observe(this);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
    requestUrl() {
      const url = new URL(window.themeVariables.routes.productRecommendationsUrl, location.origin);
      url.searchParams.set('product_id', this.getAttribute('product-id'));
      url.searchParams.set('limit', String(Math.max(1, Math.min(10, Number(this.getAttribute('recommendations-count')) || 4))));
      url.searchParams.set('section_id', this.getAttribute('section-id'));
      url.searchParams.set('intent', this.getAttribute('intent') || 'related');
      return url;
    }
    async load(url, signal) {
      const request = new AbortController();
      const abort = () => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 8000);
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) { this.showUnavailable(signal); return; }
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = doc.querySelector(this.localName);
        if (signal.aborted || !this.isConnected) return;
        const target = this.querySelector('[data-recommendation-results]');
        if (target) {
          const cards = result?.querySelector('[data-recommendation-results]');
          if (!cards?.querySelector('.complete-look-card')) { this.showUnavailable(signal); return; }
          // Keep the server-rendered heading and placeholder as the layout floor.
          target.replaceChildren(...cards.childNodes);
          this.setAttribute('data-has-products', '');
          this.removeAttribute('data-unavailable');
          this.querySelector('[data-recommendation-fallback]').hidden = true;
          this.setAttribute('data-loaded', '');
          this.setAttribute('aria-busy', 'false');
          return;
        }
        if (!result?.innerHTML.trim()) {
          if (this.getAttribute('intent') === 'complementary') this.remove();
          return;
        }
        if (this.localName === 'recently-viewed-products') {
          result.querySelectorAll('.complete-look-card').forEach((card, index) => {
            if (index >= this.productLimit) card.remove();
          });
        }
        this.replaceChildren(...result.childNodes);
        // Recently viewed uses its own history, but shares the current page's offer context.
        const prices = this.querySelector('complete-look-prices');
        const pdp = document.querySelector('modular-product[data-product-id]');
        if (prices && pdp && this.localName === 'recently-viewed-products') {
          prices.dataset.productId = pdp.dataset.productId;
          prices.updatePrices?.();
        }
      } catch (_) {
        this.showUnavailable(signal);
        // Optional merchandising must not interrupt search, product selection or buying.
      } finally {
        clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }
    showUnavailable(signal) {
      if (signal.aborted || !this.isConnected || !this.hasAttribute('data-recommendation-shell')) return;
      this.setAttribute('data-unavailable', '');
      this.setAttribute('aria-busy', 'false');
      const hasCards = !!this.querySelector('[data-recommendation-results] .complete-look-card');
      this.querySelector('[data-recommendation-fallback]').hidden = hasCards;
    }
  }
  class RecentlyViewedProducts extends RecommendedProducts {
    static get observedAttributes() { return ['products-count']; }
    get productLimit() { return Math.max(1, Math.min(20, Math.floor(Number(this.getAttribute('products-count'))) || 4)); }
    attributeChangedCallback() { if (this.isConnected) this.connectedCallback(); }
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', event => {
        if (!event.target.closest('[data-clear-recently-viewed]')) return;
        try { localStorage.removeItem('theme:recently-viewed-products'); } catch (_) {}
        document.querySelectorAll('recently-viewed-products').forEach(element => {
          element.controller?.abort();
          element.observer?.disconnect();
          element.replaceChildren();
        });
      }, { signal: this.controller.signal });
    }
    requestUrl() {
      let items;
      try { items = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]'); } catch (_) { return null; }
      if (!Array.isArray(items)) return null;
      const excluded = Number(this.getAttribute('exclude-product-id'));
      const count = this.productLimit;
      const ids = [...new Set(items)].filter(id => Number.isSafeInteger(id) && id > 0 && id !== excluded).slice(0, count);
      if (!ids.length) return null;
      const url = new URL(window.themeVariables.routes.searchUrl, location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
      url.searchParams.set('section_id', this.getAttribute('section-id'));
      return url;
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', RecommendedProducts);
  if (!customElements.get('recently-viewed-products')) customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
